import { z } from 'zod'
import { getDisabledFields } from '../../constants/tanFormDisabled'
import { numericUpTo10DigitsOptionalField, emailValidationOptional } from '@/utils/validation'
const INVALID_PAN_VALUES = ['AAAAA0000A', 'BBBBB1111B']
const ALLOWED_PAN_STATUS = ['A', 'B', 'C', 'F', 'G', 'H', 'J', 'L', 'P', 'T']

export const tanFormSchema = z
  .object({
    // Entity Details
    entityName: z
      .string()
      .min(1, 'Entity Name is required')
      .max(75)
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),
    taxDeductionAccountNumber: z
      .string()
      .trim()
      .min(1, 'TAN is required')
      .max(10, 'TAN must be exactly 10 characters')
      .regex(
        /^[A-Z]{3}[A-Z]{1}[0-9]{5}[A-Z]{1}$/,
        'Invalid TAN: Format must be 3 letters + 1 letter + 5 digits + 1 letter'
      ),
    permanentAccountNumber: z
      .string()
      .min(10, 'PAN is required')
      .max(10)
      .regex(
        /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
        'Invalid PAN format'
      ),
    branchDivision: z
      .string()
      .trim()
      .max(75, 'Branch/Division cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .optional()
      .nullable(),
    entityType: z.string().optional().nullable(),

    // Country/Region (mandatory, default: INDIA)
    country: z
      .string()
      .trim()
      .min(1, 'Country/Region is required')
      .default('INDIA'),

    // Address Details
    flatDoorBlockNumber: z
      .string()
      .trim()
      .max(25, 'Flat/Door/Block No. cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .optional()
      .nullable(),
    nameOfPremisesBuilding: z
      .string()
      .trim()
      .max(25, 'Name of the Premises/Building cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    roadStreetLane: z
      .string()
      .trim()
      .max(25, 'Road/Street/Lane cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    areaLocality: z
      .string()
      .trim()
      .max(25, { message: 'Area/Locality cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    townDistrictCity: z
      .string()
      .trim()
      .max(25, { message: 'Town/District/City cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional()
      .transform(val => val ?? ''),
    state: z.string().optional().nullable(),
    pinCode: z
      .string()
      .trim()
      .optional()
      .nullable()
      .refine(
        val => {
          if (!val) return true;
          return /^[0-9]{6}$/.test(val);
        },
        { message: 'PIN Code must be numeric and should be 6 digits' }
      ),
    // Contact Details
    stdCode: z
      .string()
      .trim()
      .max(5, 'STD Code cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD code must be numeric')
      .nullable()
      .optional(),
    telephoneNumber: numericUpTo10DigitsOptionalField('Number'),
    stdCodeAlternate: z
      .string()
      .trim()
      .max(5, 'STD Code (Alternate) cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD Code (Alternate) must be numeric')
      .nullable()
      .optional(),
    telephoneNumberAlternative: numericUpTo10DigitsOptionalField('Telephone No. (Alternate)'),
    entityEmailId: emailValidationOptional('Entity Email Id'),
    entityEmailIdAlternative: emailValidationOptional('Entity Email ID (Alternate)'),

    // Government Specific Fields
    gstnNumber: z
      .string()
      .trim()
      .max(15, 'GSTN cannot exceed 15 characters')
      .regex(
        /^$|^[A-Z0-9]{15}$/i,
        'GSTN must contain 15 alphanumeric charcaters'
      )
      .nullable()
      .optional(),
    ainNumber: z.string().max(75, 'AIN cannot exceed 75 characters').optional(),
    paoCode: z
      .string()
      .trim()
      .max(20, 'PAO Code cannot exceed 20 characters')
      .regex(/^$|^[A-Za-z0-9]{1,20}$/, 'Invalid PAO Code (alphanumeric only)')
      .nullable()
      .optional(),
    paoRegistrationNumber: z
      .string()
      .trim()
      .max(7, 'PAO Registration No. cannot exceed 7 characters')
      .regex(
        /^$|^[0-9]{1,7}$/,
        'Invalid PAO Registration Number (numeric only)'
      )
      .nullable()
      .optional(),
    ddoCode: z
      .string()
      .trim()
      .max(20, 'DDO Code cannot exceed 20 characters')
      .regex(/^$|^[A-Za-z0-9]{1,20}$/, 'Invalid DDO Code (alphanumeric only)')
      .optional(),
    ddoRegistrationNumber: z
      .string()
      .trim()
      .max(20, 'DDO Registration Number cannot exceed 20 characters')
      .regex(
        /^$|^[A-Za-z0-9]{1,20}$/,
        'Invalid DDO Registration Number (alphanumeric only)'
      )
      .optional(),
    ministryName: z.string().trim().optional().nullable(),
    ministryNameOther: z
      .string()
      .trim()
      .regex(
        /^$|^[A-Za-z0-9 ]+$/,
        'Invalid Ministry Name Other (alphanumeric only)'
      )
      .optional()
      .nullable(),
    stateMinistry: z.string().trim().optional().nullable(),
    // Mode of deposit (for AIN validation)
    modeOfDepositThroughBookAdjustment: z.boolean().optional(),

    // Responsible Person Details
    nameOfResponsiblePerson: z
      .string()
      .trim()
      .max(75, 'Name of the Responsible Person cannot exceed 75 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .optional()
      .nullable(),
    pan: z
      .string()
      .trim()
      .optional()
      .nullable()
      .refine(
        val => {
          if (!val) return true; // ✅ Skip validation if empty/null
          return val.length <= 10;
        },
        { message: 'PAN cannot exceed 10 characters' }
      )
      .refine(
        val => {
          if (!val) return true;
          return !INVALID_PAN_VALUES.includes(val.toUpperCase());
        },
        { message: 'This PAN is not accepted' }
      )
      .refine(
        val => {
          if (!val) return true;
          return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val);
        },
        {
          message: 'Invalid PAN: Format must be 5 letters + 4 digits + 1 letter',
        }
      )
      .refine(
        val => {
          if (!val || val.length !== 10) return true;
          return ALLOWED_PAN_STATUS.includes(val.charAt(3));
        },
        {
          message: `4th character must be one of: ${ALLOWED_PAN_STATUS.join(', ')}`,
        }
      )
      .refine(
        val => {
          if (!val || val.length !== 10) return true;
          return /^[A-Z]$/.test(val.charAt(4));
        },
        { message: '5th character must be a letter only' }
      ),
    designation: z
      .string()
      .trim()
      .max(20, { message: 'Designation cannot exceed 20 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .optional()
      .nullable(),

    // Responsible Person Address
    flatDoorBlockNumberRP: z
      .string()
      .trim()
      .max(25, 'Flat/Door/Block No. cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .optional()
      .nullable(),
    nameOfPremisesBuildingRP: z
      .string()
      .trim()
      .max(25, 'Name of the Premises/Building cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    roadStreetLaneRP: z
      .string()
      .trim()
      .max(25, 'Road/Street/Lane cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    areaLocalityRP: z
      .string()
      .trim()
      .max(25, { message: 'Area/Locality cannot exceed 25 characters' })
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    townDistrictCityRP: z
      .string()
      .trim()
      .max(25, 'Town/District/City cannot exceed 25 characters')
      .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
      .nullable()
      .optional(),
    stateRP: z.string().optional().nullable(),
    pinCodeRP: z
      .string()
      .trim()
      .optional()
      .nullable()
      .refine(
        val => {
          if (!val) return true;
          return /^[0-9]{6}$/.test(val);
        },
        { message: 'PIN Code must be numeric and should be 6 digits' }
      ),

    // Responsible Person Contact
    stdCodeRP: z
      .string()
      .trim()
      .max(5, 'STD Code cannot exceed 5 characters')
      .regex(/^$|^[0-9]{1,5}$/, 'STD code must be numeric')
      .nullable()
      .optional(),
    telephoneNoRP: numericUpTo10DigitsOptionalField('Number'),
    stdCodeAlternateRP: z
      .string()
      .trim()
      .max(5)
      .regex(/^$|^[0-9]{1,5}$/, 'STD code (Alternate) must be numeric')
      .nullable()
      .optional()
      .transform(val => val ?? ''),
    telephoneNoAlternateRP: numericUpTo10DigitsOptionalField('Telephone No. (Alternate)'),
    emailRP: emailValidationOptional('Email'),
    emailAlternateRP: emailValidationOptional('Email'),
    responsibleMobileNumber: numericUpTo10DigitsOptionalField('Mobile No.'),


    // Utility Fields
    addressSameAsAbove: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const disabled = getDisabledFields(data.entityType)

    // PAN validation - Entity PAN and Responsible Person PAN should not be same
    if (data.pan && data.permanentAccountNumber === data.pan) {
      ctx.addIssue({
        path: ['pan'],
        code: z.ZodIssueCode.custom,
        message: 'PAN of deductor should not be same as responsible person PAN',
      })
    }

    // Telephone validation - Either both STD and telephone should be filled or both should be empty
    const hasStdCode = data.stdCode && data.stdCode.trim() !== ''
    const hasTelephoneNumber =
      data.telephoneNumber && data.telephoneNumber.trim() !== ''

    if (
      (hasStdCode && !hasTelephoneNumber) ||
      (!hasStdCode && hasTelephoneNumber)
    ) {
      ctx.addIssue({
        path: ['telephoneNumber'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both Country code & Number should be blank or both should be filled',
      })
    }

    // Alternate Telephone validation
    const hasStdCodeAlt =
      data.stdCodeAlternate && data.stdCodeAlternate.trim() !== ''
    const hasTelephoneNumberAlt =
      data.telephoneNumberAlternative &&
      data.telephoneNumberAlternative.trim() !== ''

    if (
      (hasStdCodeAlt && !hasTelephoneNumberAlt) ||
      (!hasStdCodeAlt && hasTelephoneNumberAlt)
    ) {
      ctx.addIssue({
        path: ['telephoneNumberAlternative'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both STD code alternate & Telephone No alternate should be blank or both should be filled',
      })
    }

    // Responsible Person Telephone validation
    const hasStdCodeRP = data.stdCodeRP && data.stdCodeRP.trim() !== ''
    const hasTelephoneNumberRP =
      data.telephoneNoRP && data.telephoneNoRP.trim() !== ''

    if (
      (hasStdCodeRP && !hasTelephoneNumberRP) ||
      (!hasStdCodeRP && hasTelephoneNumberRP)
    ) {
      ctx.addIssue({
        path: ['telephoneNoRP'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both Country code & Number should be blank or both should be filled',
      })
    }

    // Responsible Person Alternate Telephone validation
    const hasStdCodeRPAlt =
      data.stdCodeAlternateRP && data.stdCodeAlternateRP.trim() !== ''
    const hasTelephoneNumberRPAlt =
      data.telephoneNoAlternateRP && data.telephoneNoAlternateRP.trim() !== ''

    if (
      (hasStdCodeRPAlt && !hasTelephoneNumberRPAlt) ||
      (!hasStdCodeRPAlt && hasTelephoneNumberRPAlt)
    ) {
      ctx.addIssue({
        path: ['telephoneNoAlternateRP'],
        code: z.ZodIssueCode.custom,
        message:
          'Either both STD code alternate & Telephone No alternate should be blank or both should be filled',
      })
    }

    // AIN validation - Mandatory if Mode of deposit Through book adjustment is "Yes"
    if (
      data.modeOfDepositThroughBookAdjustment === true &&
      (!data.ainNumber || data.ainNumber.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ainNumber'],
        code: z.ZodIssueCode.custom,
        message:
          'AIN is mandatory when Mode of deposit Through book adjustment is selected as Yes',
      })
    }

    // Ministry Name Other validation - Required if ministryName is 'OTHERS'
    if (
      data.ministryName === 'OTHERS' &&
      (!data.ministryNameOther || data.ministryNameOther.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ministryNameOther'],
        code: z.ZodIssueCode.custom,
        message:
          'Ministry Name Other is required when Others is selected as Ministry Name',
      })
    }

    if (
      !disabled.stateMinistry &&
      (!data.stateMinistry || data.stateMinistry.trim() === '')
    ) {
      ctx.addIssue({
        path: ['stateMinistry'],
        code: z.ZodIssueCode.custom,
        message: 'State/Ministry field is required for this entity type',
      })
    }

    // PAO Code required only if not disabled
    if (
      !disabled.paoCode &&
      data.entityType === 'CENTRAL GOVERNMENT' &&
      (!data.paoCode || data.paoCode.trim() === '')
    ) {
      ctx.addIssue({
        path: ['paoCode'],
        code: z.ZodIssueCode.custom,
        message: 'PAO Code is required for this entity type',
      })
    }

    // DDO Code required only if not disabled
    if (
      !disabled.ddoCode &&
      data.entityType === 'CENTRAL GOVERNMENT' &&
      (!data.ddoCode || data.ddoCode.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ddoCode'],
        code: z.ZodIssueCode.custom,
        message: 'DDO Code is required for this entity type',
      })
    }

    // Ministry Name required for Central Government and other ministry-requiring entity types
    if (
      !disabled.ministryName &&
      (!data.ministryName || data.ministryName.trim() === '')
    ) {
      ctx.addIssue({
        path: ['ministryName'],
        code: z.ZodIssueCode.custom,
        message: 'Ministry Name is required for this entity type',
      })
    }

    // Ministry Name Other required only if not disabled and ministryName is OTHERS
 
  })
